import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { errorHandler } from '@/lib/utils';
import { useSystemPageDesigns } from '../hooks/use-page-designs';
import { mapWorkspaceDesign, useDeleteWorkspacePageDesign, useWorkspacePageDesigns, } from '../hooks/use-workspace-page-designs';
import { PageDesignCard } from './page-design-card';
import { SaveAsDesignDialog } from './save-as-design-dialog';
/**
 * Card-shaped picker that drops into a campaign settings page. Selecting a
 * design fires `onPick(designId)` immediately — the parent typically wires
 * that to a campaign PUT so the change is persisted as soon as the user
 * makes a choice (no submit button; theme is mutable, low-stakes).
 */
export function PageDesignPicker({ mode, selectedId, proUnlocked, workspaceId, onPick, disabled, embedded, }) {
    const { t } = useTranslation('campaign');
    const { data: systemDesigns = [], isPending: systemPending } = useSystemPageDesigns();
    const { data: workspaceRaw = [], isPending: workspacePending } = useWorkspacePageDesigns(workspaceId);
    const { mutate: deleteDesign, isPending: isDeleting } = useDeleteWorkspacePageDesign();
    const merged = React.useMemo(() => {
        const workspace = workspaceRaw.map(mapWorkspaceDesign);
        // Workspace designs lead so the user's own brand sits above the
        // shipped system designs in the picker.
        return [...workspace, ...systemDesigns];
    }, [workspaceRaw, systemDesigns]);
    const visible = React.useMemo(() => {
        // `auto_store` is a redirect mode — the public page only renders when
        // no platform-context tracking link matches and no fallbackUrl is set,
        // in which case it falls through to `campaign.html.twig` (the classic
        // template). Themes therefore apply via the classic path; aliasing the
        // mode here keeps a single `supportedModes: ["showroom","classic",
        // "feedback"]` declaration valid for auto_store campaigns. Mirrors
        // PageDesignPreviewController which substitutes auto_store → classic
        // for preview rendering.
        const effective = mode === 'auto_store' ? 'classic' : mode;
        return merged.filter((d) => d.supportedModes.length === 0 || d.supportedModes.includes(effective));
    }, [merged, mode]);
    const isPending = systemPending || (Boolean(workspaceId) && workspacePending);
    const handleDelete = React.useCallback((design) => {
        if (!workspaceId || design.source !== 'workspace')
            return;
        deleteDesign({ workspaceId, publicId: design.id }, {
            onSuccess: (data) => toast.success(data.message),
            onError: errorHandler,
        });
    }, [deleteDesign, workspaceId]);
    const isLocked = React.useCallback((design) => {
        if (disabled)
            return true;
        return design.proOnly && !proUnlocked;
    }, [disabled, proUnlocked]);
    const selectedDesign = React.useMemo(() => visible.find((d) => d.id === selectedId) ?? visible.find((d) => d.id === 'minimal') ?? null, [visible, selectedId]);
    // The campaign points at a design id that's no longer in the picker —
    // typically because a workspace member deleted the saved design or
    // a system design was removed. The resolver silently falls back to
    // `minimal` at render-time, but the user sees the picker showing
    // `minimal` selected and assumes that's what they chose. Surface the
    // discrepancy so they know to pick a fresh one. Skipped while loading
    // (the design might just not be fetched yet) and during pending mutations.
    const showStaleSelectionNotice = !isPending && !disabled && Boolean(selectedId) && !visible.some((d) => d.id === selectedId);
    const gallery = (<div className="space-y-3">
            {showStaleSelectionNotice && (<p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
                    {t('page_design.stale_selection', {
                defaultValue: 'The previously-selected design is no longer available — your page is using the default until you pick a new one.',
            })}
                </p>)}
            {isPending ? (<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 3 }).map((_, i) => (<Skeleton key={i} className="aspect-[5/3] w-full"/>))}
                </div>) : (<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {visible.map((design) => (<PageDesignCard key={`${design.source}:${design.id}`} design={design} selected={selectedId === design.id || (!selectedId && design.id === 'minimal')} locked={isLocked(design)} onPick={(d) => onPick(d.id)} onDelete={design.source === 'workspace' && workspaceId && !isDeleting
                    ? () => handleDelete(design)
                    : undefined}/>))}
                </div>)}
            {embedded && workspaceId && (<div className="flex justify-end pt-1">
                    <SaveAsDesignDialog workspaceId={workspaceId} baseDesign={selectedDesign} proUnlocked={proUnlocked} onSaved={(design) => onPick(design.publicId)}/>
                </div>)}
        </div>);
    if (embedded) {
        return gallery;
    }
    return (<Card className="w-full">
            <CardHeader>
                <CardTitle>{t('page_design.title', { defaultValue: 'Page design' })}</CardTitle>
                <CardDescription>
                    {t('page_design.description', {
            defaultValue: 'Pick how the public page looks. Change anytime — applies on the next scan.',
        })}
                </CardDescription>
                {workspaceId && (<CardAction>
                        <SaveAsDesignDialog workspaceId={workspaceId} baseDesign={selectedDesign} proUnlocked={proUnlocked} onSaved={(design) => onPick(design.publicId)}/>
                    </CardAction>)}
            </CardHeader>
            <CardContent>{gallery}</CardContent>
        </Card>);
}
